import { Link, useLocation, useNavigate } from 'react-router'
import { useEffect, useLayoutEffect, useState, useRef } from 'react'
import toast from 'react-hot-toast'
import { useAuth } from '../../hooks/useAuth'
import { api } from '../../lib/api'
import {
  Menu,
  X,
  LayoutDashboard,
  Briefcase,
  Search,
  Sparkles,
  Crown,
  BadgeDollarSign,
  Settings,
  HelpCircle,
  MessageSquare,
  ChevronDown,
  DollarSign,
  GraduationCap,
  Info,
  ShieldCheck,
  Mail,
  Target,
  FlaskConical,
  Layers3,
} from 'lucide-react'
import { Button } from '../ui/Button'
import { cn } from '../../utils/cn'
import { motion, AnimatePresence } from 'framer-motion'
import { ThemeToggleButton } from '../theme/ThemeToggle'
import { Logo } from './Logo'
import FeedbackModal from '../feedback/FeedbackModal'
import { isNativeMobile } from '../../mobile/capacitor'
import { withMarketingQuery } from '../../utils/marketingLinks'
import SearchTrigger from '../../search/SearchTrigger'
import { isUniversalSearchEnabled } from '../../config/universalSearch'

export default function Navigation() {
  const { user, profile, signOut } = useAuth()
  const userId = user?.id ?? null
  const location = useLocation()
  const navigate = useNavigate()
  const [profileResult, setUserProfile] = useState<{
    ownerId: string
    full_name?: string
    avatar_url?: string
    is_premium?: boolean
    is_admin?: boolean
    access_status?: string
  } | null>(null)
  const userProfile = profileResult?.ownerId === userId ? profileResult : null
  const [profileLoading, setProfileLoading] = useState(true)
  const [isScrolled, setIsScrolled] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [helpMenuOpen, setHelpMenuOpen] = useState(false)
  const [productMenuOpen, setProductMenuOpen] = useState(false)
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false)
  const helpMenuRef = useRef<HTMLDivElement>(null)
  const productMenuRef = useRef<HTMLDivElement>(null)
  const mobileMenuContentRef = useRef<HTMLDivElement>(null)
  const navigationRef = useRef<HTMLElement>(null)
  const [searchHeaderReflow, setSearchHeaderReflow] = useState(false)
  const searchEnabled = isUniversalSearchEnabled()
  const nativeMobile = import.meta.env.MODE === 'mobile' || isNativeMobile()
  const pricingPath = '/pricing'
  const signupPath = withMarketingQuery('/signup', location.search)
  const needsTrialActivation = userProfile?.access_status === 'no_plan'
  const premiumActionPath = needsTrialActivation ? '/trial-activation' : pricingPath

  // Viewport breakpoints alone do not follow enlarged root text. Measure the
  // existing shell and reuse its mobile menu when the capsule no longer fits.
  // ResizeObserver is supported by the repository's older browser targets.
  useLayoutEffect(() => {
    const shell = navigationRef.current?.firstElementChild
    if (!searchEnabled || !(shell instanceof HTMLElement)) return
    const update = () => {
      const style = getComputedStyle(shell)
      const available =
        shell.clientWidth -
        parseFloat(style.paddingLeft || '0') -
        parseFloat(style.paddingRight || '0')
      const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
      if (available > 0) setSearchHeaderReflow(available <= 74 * rem)
    }
    update()
    const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update)
    resize?.observe(shell)
    const textSize = new MutationObserver(update)
    textSize.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style', 'class'],
    })
    window.addEventListener('resize', update)
    return () => {
      resize?.disconnect()
      textSize.disconnect()
      window.removeEventListener('resize', update)
    }
  }, [searchEnabled])

  // Reset mobile menu scroll position when opened
  useEffect(() => {
    if (mobileMenuOpen && mobileMenuContentRef.current) {
      mobileMenuContentRef.current.scrollTop = 0
    }
  }, [mobileMenuOpen])

  useEffect(() => {
    if (!mobileMenuOpen) return

    const previousOverflow = document.body.style.overflow
    const previousOverscrollBehavior = document.body.style.overscrollBehavior

    document.body.style.overflow = 'hidden'
    document.body.style.overscrollBehavior = 'contain'

    return () => {
      document.body.style.overflow = previousOverflow
      document.body.style.overscrollBehavior = previousOverscrollBehavior
    }
  }, [mobileMenuOpen])

  // Close help menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (helpMenuRef.current && !helpMenuRef.current.contains(event.target as Node)) {
        setHelpMenuOpen(false)
      }
      if (productMenuRef.current && !productMenuRef.current.contains(event.target as Node)) {
        setProductMenuOpen(false)
      }
    }
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setHelpMenuOpen(false)
        setProductMenuOpen(false)
        setMobileMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    let cancelled = false
    if (userId) {
      if (profile?.id === userId) {
        setUserProfile({
          ownerId: userId,
          full_name: profile.full_name || undefined,
          avatar_url: profile.avatar_url || undefined,
          is_premium: profile.is_premium,
          is_admin: profile.is_admin,
          access_status: profile.access_status,
        })
        setProfileLoading(false)
        return
      }

      // Fetch user profile for avatar, full name, and premium status
      setUserProfile(null)
      setProfileLoading(true)
      api.auth
        .getProfile()
        .then((profile) => {
          if (cancelled || profile.id !== userId) return
          setUserProfile({
            ownerId: userId,
            full_name: profile.full_name,
            avatar_url: profile.avatar_url,
            is_premium: profile.is_premium,
            is_admin: profile.is_admin,
            access_status: profile.access_status,
          })
        })
        .catch(() => {
          // Silently fail - profile fields are optional
          if (!cancelled) setUserProfile(null)
        })
        .finally(() => {
          if (!cancelled) setProfileLoading(false)
        })
    } else {
      setUserProfile(null)
      setProfileLoading(false)
    }
    return () => {
      cancelled = true
    }
  }, [userId, profile])

  const handleSignOut = async () => {
    try {
      await signOut()
      toast.success('You have been signed out successfully')
      // Redirect to home page after sign out
      navigate('/')
    } catch (error) {
      toast.error('Failed to sign out. Please try again.')
      console.error('Sign out error:', error)
    }
  }

  const isActive = (path: string) => {
    return location.pathname === path
  }

  // Get initials from email or full name
  const getInitials = () => {
    if (userProfile?.full_name) {
      return userProfile.full_name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    }
    if (user?.email) {
      return user.email.slice(0, 2).toUpperCase()
    }
    return '??'
  }

  return (
    <>
      <nav
        ref={navigationRef}
        className={cn(
          'fixed left-0 right-0 z-50 transition-all duration-300 px-4 flex justify-center',
          'top-[calc(var(--native-safe-area-top)+1.5rem)]',
          searchEnabled && 'search-header-enabled',
          searchEnabled && searchHeaderReflow && 'search-header-reflow'
        )}
      >
        <div
          className={cn(
            'w-full max-w-[1600px] mx-auto rounded-full transition-all duration-300 border',
            nativeMobile
              ? 'bg-white dark:bg-slate-900 py-4 px-6 border-slate-200 dark:border-slate-800 shadow-xl shadow-brand-900/5 dark:shadow-black/20'
              : isScrolled
                ? 'shadow-xl shadow-brand-900/5 dark:shadow-black/20 py-3 px-6 border-white/30 dark:border-white/5 bg-white/60 dark:bg-slate-900/60 backdrop-blur-xl'
                : 'bg-white/40 dark:bg-slate-900/30 backdrop-blur-md py-4 px-6 border-white/20 dark:border-white/5 shadow-sm dark:shadow-none'
          )}
        >
          <div className="search-header-grid grid grid-cols-[1fr_auto] items-center gap-4 xl:grid-cols-[auto_minmax(0,1fr)_auto]">
            {/* Logo - Dashboard for logged-in users, Landing for visitors */}
            <Link to={user ? '/dashboard' : '/'} className="flex items-center cursor-pointer group">
              <div className="group-hover:scale-105 transition-transform duration-300">
                <Logo size="lg" />
              </div>
            </Link>

            {/* Desktop Nav - Centered */}
            <div
              className={cn(
                'search-desktop-navigation hidden xl:flex items-center gap-1 justify-self-center bg-slate-100/50 dark:bg-slate-900/50 p-1 rounded-full border border-slate-200/50 dark:border-slate-800/50 dark:backdrop-blur-md',
                user && isUniversalSearchEnabled() && 'search-header-compact'
              )}
            >
              {!user ? (
                <>
                  <div className="relative" ref={productMenuRef}>
                    <button
                      type="button"
                      onClick={() => setProductMenuOpen(!productMenuOpen)}
                      className={cn(
                        'flex items-center gap-1 text-sm font-medium px-4 py-1.5 rounded-full transition-all',
                        location.pathname.startsWith('/features') ||
                          location.pathname.startsWith('/broker-sync')
                          ? 'bg-white dark:bg-amber-500/10 text-brand-900 dark:text-amber-400 shadow-sm dark:border dark:border-amber-500/20'
                          : 'text-slate-600 dark:text-slate-400 hover:text-brand-900 dark:hover:text-amber-200 hover:bg-white dark:hover:bg-white/5 hover:shadow-sm'
                      )}
                      aria-expanded={productMenuOpen}
                      aria-haspopup="true"
                      aria-controls="product-navigation-menu"
                    >
                      Product
                      <ChevronDown
                        className={cn(
                          'h-3 w-3 transition-transform',
                          productMenuOpen && 'rotate-180'
                        )}
                      />
                    </button>
                    <AnimatePresence>
                      {productMenuOpen && (
                        <motion.div
                          id="product-navigation-menu"
                          initial={{ opacity: 0, y: -10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -10, scale: 0.95 }}
                          transition={{ duration: 0.15 }}
                          className="absolute top-full left-0 mt-2 w-72 rounded-xl border border-slate-200 bg-white p-2 shadow-xl dark:border-slate-800 dark:bg-slate-900"
                        >
                          {[
                            [
                              'Feature overview',
                              '/features',
                              'Track, understand, find, test, and manage',
                            ],
                            [
                              'Portfolio analytics',
                              '/features/options-portfolio-analytics',
                              'P&L, Greeks, and multi-leg tracking',
                            ],
                            [
                              'Broker Sync',
                              '/broker-sync',
                              'Read-only sync for eligible paid accounts',
                            ],
                            [
                              'Discovery',
                              '/features/options-discovery',
                              'Strategy scans and saved screens',
                            ],
                            [
                              'Scenario Lab',
                              '/features/scenario-lab',
                              'Test a trade against your portfolio',
                            ],
                            [
                              'Wheel Tracker',
                              '/features/wheel-tracker',
                              'Follow the complete lifecycle',
                            ],
                          ].map(([label, href, description]) => (
                            <Link
                              key={href}
                              to={href}
                              onClick={() => setProductMenuOpen(false)}
                              className="block rounded-lg px-3 py-2.5 transition hover:bg-brand-50 dark:hover:bg-white/5"
                            >
                              <span className="block text-sm font-semibold text-slate-800 dark:text-slate-100">
                                {label}
                              </span>
                              <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">
                                {description}
                              </span>
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  {!nativeMobile && (
                    <Link
                      to={withMarketingQuery('/pricing', location.search)}
                      className={cn(
                        'text-sm font-medium px-4 py-1.5 rounded-full transition-all',
                        isActive('/pricing')
                          ? 'bg-white dark:bg-amber-500/10 text-brand-900 dark:text-amber-400 shadow-sm dark:border dark:border-amber-500/20'
                          : 'text-slate-600 dark:text-slate-400 hover:text-brand-900 dark:hover:text-amber-200 hover:bg-white dark:hover:bg-white/5 hover:shadow-sm'
                      )}
                    >
                      Pricing
                    </Link>
                  )}
                  <Link
                    to="/faq"
                    className="text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-brand-900 dark:hover:text-amber-200 hover:bg-white dark:hover:bg-white/5 hover:shadow-sm px-4 py-1.5 rounded-full transition-all"
                  >
                    FAQ
                  </Link>
                  <Link
                    to="/affiliate"
                    className="text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-brand-900 dark:hover:text-amber-200 hover:bg-white dark:hover:bg-white/5 hover:shadow-sm px-4 py-1.5 rounded-full transition-all"
                  >
                    Affiliate
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/dashboard"
                    className={cn(
                      'flex items-center gap-1.5 text-sm font-medium px-4 py-1.5 rounded-full transition-all',
                      isActive('/dashboard')
                        ? 'bg-white dark:bg-amber-500/10 text-brand-900 dark:text-amber-400 shadow-sm dark:border dark:border-amber-500/20'
                        : 'text-slate-600 dark:text-slate-400 hover:text-brand-900 dark:hover:text-white hover:bg-white dark:hover:bg-white/5 hover:shadow-sm'
                    )}
                  >
                    <LayoutDashboard className="h-3.5 w-3.5" />
                    Dashboard
                  </Link>
                  <Link
                    to="/portfolio"
                    className={cn(
                      'flex items-center gap-1.5 text-sm font-medium px-4 py-1.5 rounded-full transition-all',
                      isActive('/portfolio')
                        ? 'bg-white dark:bg-amber-500/10 text-brand-900 dark:text-amber-400 shadow-sm dark:border dark:border-amber-500/20'
                        : 'text-slate-600 dark:text-slate-400 hover:text-brand-900 dark:hover:text-white hover:bg-white dark:hover:bg-white/5 hover:shadow-sm'
                    )}
                  >
                    <Briefcase className="h-3.5 w-3.5" />
                    Portfolio
                  </Link>
                  <Link
                    to="/scenario-lab"
                    className={cn(
                      'flex items-center gap-1.5 text-sm font-medium px-4 py-1.5 rounded-full transition-all',
                      isActive('/scenario-lab')
                        ? 'bg-white dark:bg-amber-500/10 text-brand-900 dark:text-amber-400 shadow-sm dark:border dark:border-amber-500/20'
                        : 'text-slate-600 dark:text-slate-400 hover:text-brand-900 dark:hover:text-white hover:bg-white dark:hover:bg-white/5 hover:shadow-sm'
                    )}
                  >
                    <FlaskConical className="h-3.5 w-3.5" />
                    Scenario Lab
                  </Link>
                  <Link
                    to="/your-edge"
                    className={cn(
                      'flex items-center gap-1.5 text-sm font-medium px-4 py-1.5 rounded-full transition-all',
                      isActive('/your-edge')
                        ? 'bg-white dark:bg-amber-500/10 text-brand-900 dark:text-amber-400 shadow-sm dark:border dark:border-amber-500/20'
                        : 'text-slate-600 dark:text-slate-400 hover:text-brand-900 dark:hover:text-white hover:bg-white dark:hover:bg-white/5 hover:shadow-sm'
                    )}
                  >
                    <Target className="h-3.5 w-3.5" />
                    Your Edge
                  </Link>
                  <Link
                    to="/screener"
                    className={cn(
                      'flex items-center gap-1.5 text-sm font-medium px-4 py-1.5 rounded-full transition-all',
                      isActive('/screener') || location.pathname.startsWith('/screener/')
                        ? 'bg-white dark:bg-amber-500/10 text-brand-900 dark:text-amber-400 shadow-sm dark:border dark:border-amber-500/20'
                        : 'text-slate-600 dark:text-slate-400 hover:text-brand-900 dark:hover:text-white hover:bg-white dark:hover:bg-white/5 hover:shadow-sm'
                    )}
                  >
                    <Search className="h-3.5 w-3.5" />
                    Screener
                  </Link>
                  <Link
                    to="/discovery"
                    className={cn(
                      'flex items-center gap-1.5 text-sm font-medium px-4 py-1.5 rounded-full transition-all',
                      isActive('/discovery')
                        ? 'bg-white dark:bg-amber-500/10 text-brand-900 dark:text-amber-400 shadow-sm dark:border dark:border-amber-500/20'
                        : 'text-slate-600 dark:text-slate-400 hover:text-brand-900 dark:hover:text-white hover:bg-white dark:hover:bg-white/5 hover:shadow-sm'
                    )}
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    Discovery
                  </Link>
                  <Link
                    to="/affiliate/dashboard"
                    className={cn(
                      'flex items-center gap-1.5 text-sm font-medium px-4 py-1.5 rounded-full transition-all',
                      location.pathname.startsWith('/affiliate')
                        ? 'bg-white dark:bg-amber-500/10 text-brand-900 dark:text-amber-400 shadow-sm dark:border dark:border-amber-500/20'
                        : 'text-slate-600 dark:text-slate-400 hover:text-brand-900 dark:hover:text-white hover:bg-white dark:hover:bg-white/5 hover:shadow-sm'
                    )}
                  >
                    <BadgeDollarSign className="h-3.5 w-3.5" />
                    Affiliate
                  </Link>
                  {userProfile?.is_admin && (
                    <>
                      <Link
                        to="/admin/funnel"
                        className={cn(
                          'flex items-center gap-1.5 text-sm font-medium px-4 py-1.5 rounded-full transition-all',
                          isActive('/admin/funnel')
                            ? 'bg-white dark:bg-amber-500/10 text-brand-900 dark:text-amber-400 shadow-sm dark:border dark:border-amber-500/20'
                            : 'text-slate-600 dark:text-slate-400 hover:text-brand-900 dark:hover:text-white hover:bg-white dark:hover:bg-white/5 hover:shadow-sm'
                        )}
                      >
                        <ShieldCheck className="h-3.5 w-3.5" />
                        Admin Funnel
                      </Link>
                      <Link
                        to="/admin/email-campaigns"
                        className={cn(
                          'flex items-center gap-1.5 text-sm font-medium px-4 py-1.5 rounded-full transition-all',
                          isActive('/admin/email-campaigns')
                            ? 'bg-white dark:bg-amber-500/10 text-brand-900 dark:text-amber-400 shadow-sm dark:border dark:border-amber-500/20'
                            : 'text-slate-600 dark:text-slate-400 hover:text-brand-900 dark:hover:text-white hover:bg-white dark:hover:bg-white/5 hover:shadow-sm'
                        )}
                      >
                        <Mail className="h-3.5 w-3.5" />
                        Admin Email
                      </Link>
                      <Link
                        to="/admin/broker-sync"
                        className={cn(
                          'flex items-center gap-1.5 text-sm font-medium px-4 py-1.5 rounded-full transition-all',
                          isActive('/admin/broker-sync')
                            ? 'bg-white dark:bg-amber-500/10 text-brand-900 dark:text-amber-400 shadow-sm dark:border dark:border-amber-500/20'
                            : 'text-slate-600 dark:text-slate-400 hover:text-brand-900 dark:hover:text-white hover:bg-white dark:hover:bg-white/5 hover:shadow-sm'
                        )}
                      >
                        <ShieldCheck className="h-3.5 w-3.5" />
                        Admin Sync
                      </Link>
                    </>
                  )}
                  {/* Resources Dropdown */}
                  <div className="relative" ref={helpMenuRef}>
                    <button
                      onClick={() => setHelpMenuOpen(!helpMenuOpen)}
                      className={cn(
                        'flex items-center gap-1 text-sm font-medium px-4 py-1.5 rounded-full transition-all',
                        isActive('/faq') ||
                          isActive('/contact') ||
                          (!nativeMobile && isActive('/pricing')) ||
                          isActive('/learn') ||
                          isActive('/security') ||
                          location.pathname.startsWith('/broker-sync') ||
                          location.pathname.startsWith('/learning/') ||
                          isActive('/how-to-track-options')
                          ? 'bg-white dark:bg-amber-500/10 text-brand-900 dark:text-amber-400 shadow-sm dark:border dark:border-amber-500/20'
                          : 'text-slate-600 dark:text-slate-400 hover:text-brand-900 dark:hover:text-white hover:bg-white dark:hover:bg-white/5 hover:shadow-sm'
                      )}
                    >
                      <HelpCircle className="h-4 w-4" />
                      <span>Learn</span>
                      <ChevronDown
                        className={cn('h-3 w-3 transition-transform', helpMenuOpen && 'rotate-180')}
                      />
                    </button>
                    {/* Dropdown Menu */}
                    <AnimatePresence>
                      {helpMenuOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: -10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -10, scale: 0.95 }}
                          transition={{ duration: 0.15 }}
                          className="absolute top-full left-0 mt-2 w-56 bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-800 overflow-hidden z-50"
                        >
                          <div className="py-2">
                            <Link
                              to="/learn"
                              onClick={() => setHelpMenuOpen(false)}
                              className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-brand-50 dark:hover:bg-white/5 hover:text-brand-700 dark:hover:text-white transition-colors"
                            >
                              <GraduationCap className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                              Learning Hub
                            </Link>
                            {!nativeMobile && (
                              <Link
                                to={withMarketingQuery(pricingPath, location.search)}
                                onClick={() => setHelpMenuOpen(false)}
                                className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-brand-50 dark:hover:bg-white/5 hover:text-brand-700 dark:hover:text-white transition-colors"
                              >
                                <DollarSign className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                                Pricing
                              </Link>
                            )}
                            <Link
                              to="/about"
                              onClick={() => setHelpMenuOpen(false)}
                              className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-brand-50 dark:hover:bg-white/5 hover:text-brand-700 dark:hover:text-white transition-colors"
                            >
                              <Info className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                              About
                            </Link>
                            <div className="my-1 mx-3 h-px bg-slate-100 dark:bg-slate-800" />
                            <Link
                              to="/broker-sync"
                              onClick={() => setHelpMenuOpen(false)}
                              className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-brand-50 dark:hover:bg-white/5 hover:text-brand-700 dark:hover:text-white transition-colors"
                            >
                              <ShieldCheck className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                              Broker Sync
                            </Link>
                            <Link
                              to="/security"
                              onClick={() => setHelpMenuOpen(false)}
                              className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-brand-50 dark:hover:bg-white/5 hover:text-brand-700 dark:hover:text-white transition-colors"
                            >
                              <ShieldCheck className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                              Security
                            </Link>
                            <Link
                              to="/faq"
                              onClick={() => setHelpMenuOpen(false)}
                              className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-brand-50 dark:hover:bg-white/5 hover:text-brand-700 dark:hover:text-white transition-colors"
                            >
                              <HelpCircle className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                              FAQ
                            </Link>
                            <Link
                              to="/contact"
                              onClick={() => setHelpMenuOpen(false)}
                              className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-brand-50 dark:hover:bg-white/5 hover:text-brand-700 dark:hover:text-white transition-colors"
                            >
                              <MessageSquare className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                              Contact Us
                            </Link>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </>
              )}
            </div>

            <div className="search-desktop-utilities hidden xl:flex items-center gap-3 justify-self-end">
              {!user ? (
                <>
                  {/* Theme Toggle for non-logged in users */}
                  <SearchTrigger label />
                  <ThemeToggleButton />
                  <Link
                    to={withMarketingQuery('/signin', location.search)}
                    className="text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-brand-700 dark:hover:text-white transition-colors"
                  >
                    Log in
                  </Link>
                  <Link to={signupPath}>
                    <Button variant="dark" size="sm">
                      Get Started
                    </Button>
                  </Link>
                </>
              ) : (
                <div className="flex items-center gap-3">
                  {/* Theme Toggle */}
                  <SearchTrigger />
                  <ThemeToggleButton />

                  {/* Premium Badge for Premium Users - Only render after profile loaded to prevent flicker */}
                  {profileLoading ? (
                    // Placeholder skeleton to prevent layout shift
                    <div className="w-[88px] h-[30px] rounded-full bg-slate-100 dark:bg-slate-800 animate-pulse" />
                  ) : userProfile?.is_premium ? (
                    <Link
                      to="/settings"
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 text-white text-sm font-medium hover:from-amber-400 hover:to-amber-500 transition-all shadow-sm hover:shadow-md dark:shadow-amber-500/20"
                    >
                      <Crown className="h-3.5 w-3.5" />
                      <span>Premium</span>
                    </Link>
                  ) : nativeMobile ? null : (
                    /* Upgrade Button for Free Users */
                    <Link
                      to={premiumActionPath}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-brand-600 to-brand-700 text-white text-sm font-medium hover:from-brand-500 hover:to-brand-600 transition-all shadow-sm hover:shadow-md"
                    >
                      <Sparkles className="h-3.5 w-3.5" />
                      <span>{needsTrialActivation ? 'Start trial' : 'Upgrade'}</span>
                    </Link>
                  )}

                  {/* User Avatar */}
                  <div className="flex items-center gap-2">
                    {userProfile?.avatar_url ? (
                      <img
                        src={userProfile.avatar_url}
                        alt="Profile"
                        className="h-8 w-8 rounded-full ring-2 ring-gray-200 dark:ring-slate-700"
                      />
                    ) : (
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-600 dark:bg-gradient-to-br dark:from-amber-500 dark:to-amber-700 text-xs font-semibold text-white ring-2 ring-gray-200 dark:ring-slate-700 dark:shadow-lg dark:shadow-amber-900/20">
                        {getInitials()}
                      </div>
                    )}
                  </div>

                  <Link
                    to="/settings"
                    className="p-2 text-slate-500 dark:text-slate-400 hover:text-brand-700 dark:hover:text-white transition-colors"
                    aria-label="Settings"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </Link>

                  <button
                    onClick={() => void handleSignOut()}
                    className="text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-brand-700 dark:hover:text-white transition-colors"
                  >
                    Sign out
                  </button>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <div className="search-mobile-utilities xl:hidden flex items-center gap-2 justify-self-end">
              <SearchTrigger onOpen={() => setMobileMenuOpen(false)} />
              <ThemeToggleButton />
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="inline-flex h-11 w-11 items-center justify-center text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white bg-slate-100 dark:bg-slate-800/50 rounded-full"
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={mobileMenuOpen}
              >
                {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -20 }}
              className="search-mobile-menu-overlay fixed top-[calc(var(--native-safe-area-top)+6rem)] left-4 right-4 z-[60] xl:hidden bg-white dark:bg-slate-900/95 dark:backdrop-blur-xl rounded-2xl shadow-2xl border border-slate-100 dark:border-slate-800 overflow-hidden max-h-[calc(100dvh-var(--native-safe-area-top)-7.5rem)]"
            >
              <div
                ref={mobileMenuContentRef}
                className="p-4 space-y-4 overflow-y-auto overscroll-contain max-h-[calc(100dvh-var(--native-safe-area-top)-8.75rem)] [-webkit-overflow-scrolling:touch] [touch-action:pan-y] pb-safe"
              >
                <div className="flex flex-col gap-2">
                  {!user ? (
                    <>
                      <p className="px-4 py-1 text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                        Product
                      </p>
                      <Link
                        to="/features"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-2 rounded-xl px-4 py-3 text-lg font-medium text-slate-600 transition-colors hover:bg-brand-50 hover:text-brand-700 dark:text-slate-300 dark:hover:bg-white/5 dark:hover:text-white"
                      >
                        <Layers3 className="h-4 w-4" />
                        Feature overview
                      </Link>
                      <Link
                        to="/features/options-portfolio-analytics"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block rounded-xl px-4 py-3 text-lg font-medium text-slate-600 transition-colors hover:bg-brand-50 hover:text-brand-700 dark:text-slate-300 dark:hover:bg-white/5 dark:hover:text-white"
                      >
                        Portfolio analytics
                      </Link>
                      <Link
                        to="/features/options-discovery"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block text-lg font-medium text-slate-600 dark:text-slate-300 hover:text-brand-700 dark:hover:text-white hover:bg-brand-50 dark:hover:bg-white/5 px-4 py-3 rounded-xl transition-colors"
                      >
                        Discovery
                      </Link>
                      <Link
                        to="/broker-sync"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block text-lg font-medium text-slate-600 dark:text-slate-300 hover:text-brand-700 dark:hover:text-white hover:bg-brand-50 dark:hover:bg-white/5 px-4 py-3 rounded-xl transition-colors"
                      >
                        Broker Sync
                      </Link>
                      <Link
                        to="/features/scenario-lab"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block text-lg font-medium text-slate-600 dark:text-slate-300 hover:text-brand-700 dark:hover:text-white hover:bg-brand-50 dark:hover:bg-white/5 px-4 py-3 rounded-xl transition-colors"
                      >
                        Scenario Lab
                      </Link>
                      <Link
                        to="/features/wheel-tracker"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block text-lg font-medium text-slate-600 dark:text-slate-300 hover:text-brand-700 dark:hover:text-white hover:bg-brand-50 dark:hover:bg-white/5 px-4 py-3 rounded-xl transition-colors"
                      >
                        Wheel Tracker
                      </Link>
                      <div className="my-2 h-px bg-slate-100 dark:bg-slate-800" />
                      {!nativeMobile && (
                        <Link
                          to={withMarketingQuery('/pricing', location.search)}
                          onClick={() => setMobileMenuOpen(false)}
                          className="block rounded-xl px-4 py-3 text-lg font-medium text-slate-600 transition-colors hover:bg-brand-50 hover:text-brand-700 dark:text-slate-300 dark:hover:bg-white/5 dark:hover:text-white"
                        >
                          Pricing
                        </Link>
                      )}
                      <Link
                        to="/faq"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block rounded-xl px-4 py-3 text-lg font-medium text-slate-600 transition-colors hover:bg-brand-50 hover:text-brand-700 dark:text-slate-300 dark:hover:bg-white/5 dark:hover:text-white"
                      >
                        FAQ
                      </Link>
                      <Link
                        to="/affiliate"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block rounded-xl px-4 py-3 text-lg font-medium text-slate-600 transition-colors hover:bg-brand-50 hover:text-brand-700 dark:text-slate-300 dark:hover:bg-white/5 dark:hover:text-white"
                      >
                        Affiliate
                      </Link>
                    </>
                  ) : (
                    <>
                      <Link
                        to="/dashboard"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-2 text-lg font-medium text-slate-600 dark:text-slate-300 hover:text-brand-700 dark:hover:text-white hover:bg-brand-50 dark:hover:bg-white/5 px-4 py-3 rounded-xl transition-colors"
                      >
                        <LayoutDashboard className="h-4 w-4" />
                        Dashboard
                      </Link>
                      <Link
                        to="/portfolio"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-2 text-lg font-medium text-slate-600 dark:text-slate-300 hover:text-brand-700 dark:hover:text-white hover:bg-brand-50 dark:hover:bg-white/5 px-4 py-3 rounded-xl transition-colors"
                      >
                        <Briefcase className="h-4 w-4" />
                        Portfolio
                      </Link>
                      <Link
                        to="/scenario-lab"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-2 text-lg font-medium text-slate-600 dark:text-slate-300 hover:text-brand-700 dark:hover:text-white hover:bg-brand-50 dark:hover:bg-white/5 px-4 py-3 rounded-xl transition-colors"
                      >
                        <FlaskConical className="h-4 w-4" />
                        Scenario Lab
                      </Link>
                      <Link
                        to="/your-edge"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-2 text-lg font-medium text-slate-600 dark:text-slate-300 hover:text-brand-700 dark:hover:text-white hover:bg-brand-50 dark:hover:bg-white/5 px-4 py-3 rounded-xl transition-colors"
                      >
                        <Target className="h-4 w-4" />
                        Your Edge
                      </Link>
                      <Link
                        to="/screener"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-2 text-lg font-medium text-slate-600 dark:text-slate-300 hover:text-brand-700 dark:hover:text-white hover:bg-brand-50 dark:hover:bg-white/5 px-4 py-3 rounded-xl transition-colors"
                      >
                        <Search className="h-4 w-4" />
                        Screener
                      </Link>
                      <Link
                        to="/discovery"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-2 text-lg font-medium text-slate-600 dark:text-slate-300 hover:text-brand-700 dark:hover:text-white hover:bg-brand-50 dark:hover:bg-white/5 px-4 py-3 rounded-xl transition-colors"
                      >
                        <Sparkles className="h-4 w-4" />
                        Discovery
                      </Link>
                      <Link
                        to="/settings"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-2 text-lg font-medium text-slate-600 dark:text-slate-300 hover:text-brand-700 dark:hover:text-white hover:bg-brand-50 dark:hover:bg-white/5 px-4 py-3 rounded-xl transition-colors"
                      >
                        <Settings className="h-4 w-4" />
                        Settings
                      </Link>
                      <Link
                        to="/affiliate/dashboard"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-2 text-lg font-medium text-slate-600 dark:text-slate-300 hover:text-brand-700 dark:hover:text-white hover:bg-brand-50 dark:hover:bg-white/5 px-4 py-3 rounded-xl transition-colors"
                      >
                        <BadgeDollarSign className="h-4 w-4" />
                        Affiliate
                      </Link>
                      {userProfile?.is_admin && (
                        <>
                          <Link
                            to="/admin/funnel"
                            onClick={() => setMobileMenuOpen(false)}
                            className="flex items-center gap-2 text-lg font-medium text-slate-600 dark:text-slate-300 hover:text-brand-700 dark:hover:text-white hover:bg-brand-50 dark:hover:bg-white/5 px-4 py-3 rounded-xl transition-colors"
                          >
                            <ShieldCheck className="h-4 w-4" />
                            Admin Funnel
                          </Link>
                          <Link
                            to="/admin/email-campaigns"
                            onClick={() => setMobileMenuOpen(false)}
                            className="flex items-center gap-2 text-lg font-medium text-slate-600 dark:text-slate-300 hover:text-brand-700 dark:hover:text-white hover:bg-brand-50 dark:hover:bg-white/5 px-4 py-3 rounded-xl transition-colors"
                          >
                            <Mail className="h-4 w-4" />
                            Admin Email
                          </Link>
                          <Link
                            to="/admin/broker-sync"
                            onClick={() => setMobileMenuOpen(false)}
                            className="flex items-center gap-2 text-lg font-medium text-slate-600 dark:text-slate-300 hover:text-brand-700 dark:hover:text-white hover:bg-brand-50 dark:hover:bg-white/5 px-4 py-3 rounded-xl transition-colors"
                          >
                            <ShieldCheck className="h-4 w-4" />
                            Admin Sync
                          </Link>
                        </>
                      )}
                      <button
                        onClick={() => {
                          setMobileMenuOpen(false)
                          setIsFeedbackModalOpen(true)
                        }}
                        className="flex items-center gap-2 text-lg font-medium text-slate-600 dark:text-slate-300 hover:text-brand-700 dark:hover:text-white hover:bg-brand-50 dark:hover:bg-white/5 px-4 py-3 rounded-xl transition-colors w-full text-left"
                      >
                        <MessageSquare className="h-4 w-4" />
                        Feedback
                        <span className="ml-auto text-xs text-slate-400 dark:text-slate-500">
                          Bug / Feature
                        </span>
                      </button>
                      <div className="h-px bg-slate-100 dark:bg-slate-800 my-2"></div>
                      <p className="px-4 py-1 text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                        Learn
                      </p>
                      <Link
                        to="/learn"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-3 text-base font-medium text-slate-500 dark:text-slate-400 hover:text-brand-700 dark:hover:text-white hover:bg-brand-50 dark:hover:bg-white/5 px-4 py-3 rounded-xl transition-colors"
                      >
                        <GraduationCap className="h-5 w-5" />
                        Learning Hub
                      </Link>
                      {!nativeMobile && (
                        <Link
                          to={withMarketingQuery(pricingPath, location.search)}
                          onClick={() => setMobileMenuOpen(false)}
                          className="flex items-center gap-3 text-base font-medium text-slate-500 dark:text-slate-400 hover:text-brand-700 dark:hover:text-white hover:bg-brand-50 dark:hover:bg-white/5 px-4 py-3 rounded-xl transition-colors"
                        >
                          <DollarSign className="h-5 w-5" />
                          Pricing
                        </Link>
                      )}
                      <Link
                        to="/about"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-3 text-base font-medium text-slate-500 dark:text-slate-400 hover:text-brand-700 dark:hover:text-white hover:bg-brand-50 dark:hover:bg-white/5 px-4 py-3 rounded-xl transition-colors"
                      >
                        <Info className="h-5 w-5" />
                        About
                      </Link>
                      <Link
                        to="/broker-sync"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-3 text-base font-medium text-slate-500 dark:text-slate-400 hover:text-brand-700 dark:hover:text-white hover:bg-brand-50 dark:hover:bg-white/5 px-4 py-3 rounded-xl transition-colors"
                      >
                        <ShieldCheck className="h-5 w-5" />
                        Broker Sync
                      </Link>
                      <Link
                        to="/security"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-3 text-base font-medium text-slate-500 dark:text-slate-400 hover:text-brand-700 dark:hover:text-white hover:bg-brand-50 dark:hover:bg-white/5 px-4 py-3 rounded-xl transition-colors"
                      >
                        <ShieldCheck className="h-5 w-5" />
                        Security
                      </Link>
                      <Link
                        to="/faq"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-3 text-base font-medium text-slate-500 dark:text-slate-400 hover:text-brand-700 dark:hover:text-white hover:bg-brand-50 dark:hover:bg-white/5 px-4 py-3 rounded-xl transition-colors"
                      >
                        <HelpCircle className="h-5 w-5" />
                        FAQ
                      </Link>
                      <Link
                        to="/contact"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-3 text-base font-medium text-slate-500 dark:text-slate-400 hover:text-brand-700 dark:hover:text-white hover:bg-brand-50 dark:hover:bg-white/5 px-4 py-3 rounded-xl transition-colors"
                      >
                        <MessageSquare className="h-5 w-5" />
                        Contact
                      </Link>
                    </>
                  )}
                </div>
                <div className="h-px bg-slate-100 dark:bg-slate-800 w-full"></div>
                <div className="flex flex-col gap-3">
                  {!user ? (
                    <>
                      <Link
                        to={withMarketingQuery('/signin', location.search)}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Button variant="secondary" className="w-full justify-center">
                          Log in
                        </Button>
                      </Link>
                      <Link to={signupPath} onClick={() => setMobileMenuOpen(false)}>
                        <Button variant="dark" className="w-full justify-center">
                          Get Started
                        </Button>
                      </Link>
                    </>
                  ) : (
                    <>
                      {profileLoading ? (
                        // Placeholder skeleton for mobile
                        <div className="w-full h-10 rounded-lg bg-slate-100 dark:bg-slate-800 animate-pulse" />
                      ) : userProfile?.is_premium ? (
                        <Link to="/settings" onClick={() => setMobileMenuOpen(false)}>
                          <Button className="w-full justify-center gap-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500">
                            <Crown className="h-4 w-4" />
                            Premium Member
                          </Button>
                        </Link>
                      ) : nativeMobile ? null : (
                        <Link to={premiumActionPath} onClick={() => setMobileMenuOpen(false)}>
                          <Button className="w-full justify-center gap-2 bg-gradient-to-r from-brand-600 to-brand-700 hover:from-brand-500 hover:to-brand-600">
                            <Sparkles className="h-4 w-4" />
                            {needsTrialActivation ? 'Start trial' : 'Upgrade to Premium'}
                          </Button>
                        </Link>
                      )}
                      <Button
                        variant="secondary"
                        className="w-full justify-center"
                        onClick={() => {
                          void handleSignOut()
                          setMobileMenuOpen(false)
                        }}
                      >
                        Sign out
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Feedback Modal for mobile navigation */}
      <FeedbackModal isOpen={isFeedbackModalOpen} onClose={() => setIsFeedbackModalOpen(false)} />
    </>
  )
}
